import React, { useMemo } from 'react';
import find from 'lodash/find';
import map from 'lodash/map';
import styled from 'styled-components';

import AnswerStrokeIcon from '@ad-scripts/presentation/components/common/icons/stroke/answers-stroke';
import DeleteButton from 'src/presentation/components/pages/shared/DeleteButton';
import { EmptyStateWrapper } from 'src/presentation/components/pages/shared/TableList';
import { StyledPointerIconWrap } from 'src/presentation/components/pages/shared/DraggableTable';
import Table, { RowActionContainer, StyledTH } from 'src/presentation/components/pages/shared/Table';

const MultiSelectSelectedOptionsTable = ({
  allOptions,
  memoizedLabels,
  onChangeOptionPosition,
  onDelete,
  selectedValues,
}: {
  allOptions?;
  memoizedLabels?;
  onChangeOptionPosition?;
  onDelete?;
  selectedValues?;
}): JSX.Element => {
  const optionsWithLabelsOrIds = useMemo(
    () =>
      map(selectedValues, id => {
        const item = find(allOptions, item => item.value === id);
        // memoizedLabels is used in case if we have backend search,
        // to have previous labels we should store them
        // in case if we can't find label ID will be instead
        return item ? { ...item, id: item.value } : { id, value: id, label: memoizedLabels[id] || id };
      }),
    [allOptions, selectedValues, memoizedLabels]
  );

  const rowActionRender = ({ item }) => <DeleteButton id={item.id} onClick={() => onDelete(item.id)} />;

  const handleDragEnd = props => {
    const { currentItemId, replaceItemId } = props;
    onChangeOptionPosition && onChangeOptionPosition(currentItemId, replaceItemId);
  };

  return (
    <>
      <StyledTable
        className="multiSelect-table"
        columnConfig={[
          {
            attr: 'label',
            label: 'Name',
          },
        ]}
        dataAid="multiSelect-table-selected-options"
        items={optionsWithLabelsOrIds}
        dragEndHandler={handleDragEnd}
        renderRowActions={rowActionRender}
      />

      {!optionsWithLabelsOrIds.length && (
        <EmptyStateWrapper data-aid="empty-state">
          <EmptyResultIcon />
          Search for answers and they{"'"}ll appear here
        </EmptyStateWrapper>
      )}
    </>
  );
};

const EmptyResultIcon = styled(AnswerStrokeIcon)`
  fill: ${({ theme }) => theme.colors.gray600};
  margin-bottom: 20px;
  width: ${p => p.theme.iconSize.medium};
`;

const StyledTable = styled(Table)`
  ${StyledTH}:first-child,${StyledTH}:last-child {
    width: 32px;
  }

  ${RowActionContainer} * {
    visibility: visible;
  }

  ${StyledPointerIconWrap} {
    visibility: visible;
  }
`;

export default MultiSelectSelectedOptionsTable;
